import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ContextualHelpIcon from 'components/help/ContextualHelpIcon';

import BellOutlined from '@ant-design/icons/BellOutlined';

import alertService from 'api/alertService';

function alertLink(alert) {
  if (alert.entityType === 'deal' && alert.entityId) return `/deals/${alert.entityId}`;
  return null;
}

function resolveAlertTitle(alert, t) {
  const key = `alerts.types.${alert.alertType}`;
  const translated = t(key, { defaultValue: '' });
  return translated || alert.title;
}

function resolveAlertBody(alert, t) {
  const staleMatch = /^No activity for (\d+) days$/.exec(String(alert.body ?? ''));
  if (staleMatch && alert.alertType === 'stale_deal') {
    return t('alerts.bodies.stale_deal', { days: staleMatch[1] });
  }
  const lowScoreMatch = /^Score: (\d+) —/.exec(String(alert.body ?? ''));
  if (lowScoreMatch && alert.alertType === 'low_deal_score') {
    return t('alerts.bodies.low_deal_score', { score: lowScoreMatch[1] });
  }
  return alert.body;
}

export default function AlertsPanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = () => {
    alertService
      .list()
      .then((res) => {
        setAlerts(res.data.alerts ?? []);
        setUnreadCount(res.data.unreadCount ?? 0);
      })
      .catch(() => {
        setAlerts([]);
        setUnreadCount(0);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpen = () => {
    setOpen(true);
    load();
  };

  const handleMarkRead = async (id) => {
    await alertService.markRead(id);
    load();
  };

  const handleMarkAll = async () => {
    await alertService.markAllRead();
    load();
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label={t('alerts.title')}>
        <Badge badgeContent={unreadCount} color="error">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 360, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h6">{t('alerts.title')}</Typography>
              <ContextualHelpIcon contextKey="alerts.panel" />
            </Stack>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAll}>
                {t('alerts.markAllRead')}
              </Button>
            )}
          </Stack>
          <Divider />
          <List>
            {alerts.length === 0 && (
              <ListItem>
                <ListItemText primary={t('alerts.empty')} />
              </ListItem>
            )}
            {alerts.map((alert) => {
              const to = alertLink(alert);
              const title = resolveAlertTitle(alert, t);
              const body = resolveAlertBody(alert, t);
              const primary = to ? (
                <Link to={to} onClick={() => setOpen(false)}>
                  {title}
                </Link>
              ) : (
                title
              );
              return (
                <ListItem
                  key={alert.id}
                  secondaryAction={
                    !alert.readAt ? (
                      <Button size="small" onClick={() => handleMarkRead(alert.id)}>
                        {t('alerts.markRead')}
                      </Button>
                    ) : null
                  }
                >
                  <ListItemText primary={primary} secondary={body} sx={{ opacity: alert.readAt ? 0.6 : 1 }} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
